/**
 * Gestion réactive de l'état pour le module Produits.
 *
 * Ce module contient :
 *
 *  **Pagination & Recherche**
 * - pageProduct : numéro de page actuel.
 * - searchQuery / searchQueryProduct : termes de recherche pour filtrer les produits.
 *
 *  **Filtres Produits**
 * - designation_product_filter : filtre par désignation.
 * - category_product_filter : filtre par catégorie.
 * - checkedValue : valeur sélectionnée dans une liste (checkbox / radio).
 *
 *  **Informations d'affichage**
 * - titlePageProduct : titre affiché sur la page produits.
 * - descriptionPageProduct : description de la page produits.
 *
 *  **États d'action sur un produit**
 * - isReadProduct : indique si un produit est en mode lecture.
 * - isUpdatedProduct : indique si un produit est en cours de mise à jour.
 * - isDeletedProduct : indique si un produit est supprimé.
 * - isNewProduct : indique si l’on crée un nouveau produit.
 *
 *  **Étapes du processus (Stepper)**
 * - indexOfStepProduct / indexOfStepTwoProduct / indexOfStepThreeProduct / indexOfStepFourProduct : index des étapes.
 * - stepOneActive / stepTwoActive / stepThreeActive / stepFourActive : étapes actives dans l’UI.
 *
 *  **Formulaire — Étape 1**
 * - form_product_step_one : premières informations du produit.
 *
 *  **Fichier & Preview**
 * - file : fichier image sélectionné.
 * - previewUrl : prévisualisation de l’image.
 *
 *  **Messages & Erreurs**
 * - errorMessage : message d’erreur général.
 *
 *  **Données dynamiques des étapes**
 * - arraysStepOne / arraysStepTwo / arraysStepThree / arraysStepFour : tableaux réactifs de données pour chaque étape.
 *
 *  **Catégorie produit**
 * - category_product : catégories liées.
 *
 *  **Incrémentation**
 * - sateIncrement : compteur utilisé dans la génération de champs dynamiques.
 *
 *  **Champs dynamiques**
 * - dynamicOptions : options dynamiques (ex : variantes simples).
 *
 *  **Options supplémentaires dynamiques (Étape 2)**
 * - dynamicExtraOptions : gestion complète des extra-options d’un produit (nom, prix, type, quantité, conditions, etc.).
 *
 *  **Autres**
 * - NumberOfTotalField : nombre total de champs à afficher.
 * - productId : identifiant du produit en cours (lecture, update...).
 *
 * Utilise ref() et reactive() de Vue 3.
 */
import type { IProductStepTwo } from '@/types/product.types';
import { reactive, ref } from 'vue';
import { useStorage } from '@vueuse/core';

export const currentLocale = useStorage<'fr' | 'en' | 'nl'>('locale', 'fr');

export const pageProduct = ref(1);
export const sortProduct = ref<'newer' | 'favorite' | 'category'>('newer');
export const designation_product_filter = ref<string[]>([]);
export const category_product_filter = ref<string[]>([]);
export const checkedValue = ref<any>();

export const titlePageProduct = ref('Tous les  produits');
export const descriptionPageProduct = ref('Produit / Liste des produits');
export const isReadProduct = ref(false);
export const isUpdatedProduct = ref(false);
export const isDeletedProduct = ref(false);
export const isNewProduct = ref(false);
export const indexOfStepProduct = ref(1);
export const indexOfStepTwoProduct = ref(2);
export const indexOfStepThreeProduct = ref(3);
export const indexOfStepFourProduct = ref(4);
export const stepOneActive = ref(true);
export const stepTwoActive = ref(false);
export const stepThreeActive = ref(false);
export const stepFourActive = ref(false);
export const form = reactive({
  prixTTC: '',
  tva: '',
  tvaId: '',
  hasVariantes: false,
});
export const form_product_step_oneIdImage = ref<string | null>();
export const form_product_step_one = reactive({
  designation: '',
  price: '',
  taxe: '',
  image: null,
  categorie: '',
  description: '',
  nameExtra: '',
  typeQty: '',
  valueQty: '',
  conditionExtra: '',
  taxe_teakeaway: '',
  taxte_onsite: '',
  SelectRestaurant: '62655039-0c78-41dd-9f1a-ea33ab548062',
  typeExtra: '',
  ean: '',
  type: 'extra',
});
export const file = ref<File | null>(null);
export const previewUrl = ref<string | null>(null);
export const errorMessage = ref<string | null>(null);
export const arraysStepOne = ref<any[]>([]);
export const arraysStepTwo = ref<any[]>([]);
export const arraysStepThree = ref<any[]>([]);
export const arraysStepFour = ref<any[]>([]);
export const category_product = ref<string[]>([]);
export const sateIncrement = ref(1);

export const dynamicOptions = ref<{ name: string }[]>([{ name: '' }]);
export const isShowedUpselling = ref(false);
export const dynamicExtraOptions = ref<IProductStepTwo[]>([
  {
    designation: '',
    price: '',
    taxe: '',
    image: '',
    categorie: '',
    description: '',
    nameExtra: '',
    typeQty: '',
    valueQty: null,
    conditionExtra: '',
    required: false,
    extraOptions: [
      {
        name: '',
        price: '',
      },
    ],
  },
]);

export const NumberOfTotalField = ref(6);

export const productId = ref(null);

export const searchQuery = ref<string>('');
export const searchQueryProduct = ref('');
export const backdropOpacity = ref(0.2);
export const isMaskDrawerClosed = ref(false);
export const inputValue = ref<null | string>(null);
export const isFixedPositionTitle = ref(true);
export const styleOfTimesIcon = ref('text-white hidden');
export const styleFixedPositionTitle = ref(
  'z-[4000] flex items-center bg-white relative w-full top-0 h-[80px] py-4 shadow-none backdrop-blur-md',
);

export const isLoging = ref<any>(false);
export const isTruncatedStyle = ref(true);
export const selected = ref(false);

export const oldFromData = ref<any[]>([]);

// ─── AddonGroups & Addons ──────────────────────────────────────
export const addonGroupId = ref<string | null>(null);
export const addonId = ref<string | null>(null);

// ─── Variantes partagées (Step2 → Step5) ──────────────────────
export interface IVariante {
  id: number;
  nomFr: string;
  nomNl: string;
  nomEn: string;
  prix: string;
  checked: boolean;
  editing: boolean;
}
export const productVariantes = ref<IVariante[]>([]);
export const productVarianteNextId = ref(0);

export interface IProductVariantItem {
  id: string;
  label: string;
  price: number;
  isDefault: boolean;
  status: string;
}
export const productVariantsList = ref<IProductVariantItem[]>([]);
